import { useState, useEffect, useCallback, useRef, useMemo } from 'preact/hooks';
import { X, Check, MagnifyingGlass, CaretDown } from '@phosphor-icons/react';
import { api, type RegistryItem, type SpotPosition, type SpotPositionInput } from '../../services/api.js';
import { useToast } from '../shared/Toast.js';
import { suggestTpPrices, formatUsdPrice } from './spotUtils.js';
import { lockScroll, unlockScroll } from '../../utils/scrollLock.js';

interface PositionModalProps {
  position: SpotPosition | null;
  onClose: () => void;
  onSaved: () => void;
}

interface PickedCoin {
  id: string;
  symbol: string;
  name: string;
  image?: string;
}

export function PositionModal({ position, onClose, onSaved }: PositionModalProps) {
  const isEdit = position !== null;
  const { show: toast } = useToast();

  const [coin, setCoin] = useState<PickedCoin | null>(
    position ? { id: position.coinId, symbol: position.symbol, name: position.name, image: position.image } : null,
  );
  const [currentPrice, setCurrentPrice] = useState<number | null>(
    position ? position.currentPriceUsd : null,
  );

  const [isFutures, setIsFutures] = useState(position?.isFutures ?? false);
  const [qty,           setQty]           = useState(position ? String(position.qty) : '');
  const [totalSpentUsd, setTotalSpentUsd] = useState(position ? String(position.totalSpentUsd) : '');
  const [tp1, setTp1] = useState(position?.tp1Price != null ? String(position.tp1Price) : '');
  const [tp2, setTp2] = useState(position?.tp2Price != null ? String(position.tp2Price) : '');
  const [tp3, setTp3] = useState(position?.tp3Price != null ? String(position.tp3Price) : '');
  const [closePrice, setClosePrice] = useState(position?.closePrice != null ? String(position.closePrice) : '');
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<RegistryItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close the coin dropdown when clicking outside of it
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [pickerOpen]);

  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (coin) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      api.searchRegistry(search, 1, 20)
        .then((res) => setResults(res.items))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [search, coin]);

  const pickCoin = useCallback(async (c: RegistryItem) => {
    setCoin({ id: c.id, symbol: c.symbol, name: c.name });
    try {
      const market = await api.getRates([c.id]);
      const price = market[0]?.current_price ?? null;
      if (price) {
        setCurrentPrice(price);
        const fmt = (v: number) => v >= 1 ? v.toFixed(2) : v.toFixed(6);
        if (!tp1) setTp1(fmt(price * 1.10));
        if (!tp2) setTp2(fmt(price * 1.25));
        if (!tp3) setTp3(fmt(price * 1.50));
      }
      if (market[0]?.image) {
        setCoin((prev) => prev ? { ...prev, image: market[0].image } : prev);
      }
    } catch {
      // best-effort
    }
  }, [tp1, tp2, tp3]);

  const avgPrice = useMemo(() => {
    const q = parseFloat(qty);
    const s = parseFloat(totalSpentUsd);
    return Number.isFinite(q) && Number.isFinite(s) && q > 0 && s > 0 ? s / q : null;
  }, [qty, totalSpentUsd]);

  const fillFromAvg = useCallback(() => {
    if (avgPrice === null) return;
    const [a, b, c] = suggestTpPrices(avgPrice);
    const fmt = (v: number) => v >= 1 ? v.toFixed(2) : v.toFixed(6);
    setTp1(fmt(a)); setTp2(fmt(b)); setTp3(fmt(c));
  }, [avgPrice]);

  const parseTp = (v: string): number | null => {
    const trimmed = v.trim();
    if (trimmed === '') return null;
    const n = parseFloat(trimmed);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  // Futures close-target direction, derived from the live price the user sees.
  const closeDirection = useMemo<'up' | 'down' | null>(() => {
    const target = parseFloat(closePrice);
    if (!Number.isFinite(target) || target <= 0 || currentPrice == null || currentPrice <= 0) return null;
    return target > currentPrice ? 'up' : 'down';
  }, [closePrice, currentPrice]);

  const validation = useMemo(() => {
    if (!coin) return 'Выберите монету';
    const q = parseFloat(qty);
    if (!Number.isFinite(q) || q <= 0) return 'Введите количество > 0';
    const s = parseFloat(totalSpentUsd);
    if (!Number.isFinite(s) || s <= 0) return 'Введите потраченную сумму > 0';
    if (!isFutures) {
      for (const [label, v] of [['TP1', tp1], ['TP2', tp2], ['TP3', tp3]] as const) {
        const t = v.trim();
        if (t !== '') {
          const n = parseFloat(t);
          if (!Number.isFinite(n) || n <= 0) return `${label}: неверная цена`;
        }
      }
    } else if (closePrice.trim() !== '') {
      const n = parseFloat(closePrice);
      if (!Number.isFinite(n) || n <= 0) return 'Цена закрытия: неверная цена';
    }
    return null;
  }, [coin, qty, totalSpentUsd, tp1, tp2, tp3, isFutures, closePrice]);

  const submit = useCallback(async () => {
    if (validation || !coin) return;
    setSaving(true);
    try {
      const payload: SpotPositionInput = {
        id: position?.id,
        coinId: coin.id,
        symbol: coin.symbol,
        name: coin.name,
        image: coin.image ?? '',
        isFutures,
        qty: parseFloat(qty),
        totalSpentUsd: parseFloat(totalSpentUsd),
        tp1Price: isFutures ? null : parseTp(tp1),
        tp2Price: isFutures ? null : parseTp(tp2),
        tp3Price: isFutures ? null : parseTp(tp3),
        closePrice: isFutures ? parseTp(closePrice) : null,
        closeDirection: isFutures ? closeDirection : null,
      };
      await api.savePosition(payload);
      toast(isEdit ? 'Позиция обновлена' : 'Позиция добавлена', 'success');
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка сохранения';
      toast(`Ошибка: ${msg}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [validation, coin, position?.id, isFutures, qty, totalSpentUsd, tp1, tp2, tp3, closePrice, closeDirection, isEdit, onSaved, toast]);

  return (
    <>
      <div class="modal-backdrop" onClick={onClose} />
      <div class="modal" role="dialog" aria-modal="true">
        <div class="modal-header">
          <span class="modal-title">{isEdit ? `Изменить — ${position?.name}` : 'Новая позиция'}</span>
          <button class="btn-icon" onClick={onClose} style={{ width: '28px', height: '28px' }} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div class="spot-modal-body">
          <div class="spot-form-group">
            <label class="spot-form-label">Монета</label>
            {coin ? (
              <div class="spot-coin-selected">
                {coin.image
                  ? <img src={coin.image} alt={coin.symbol} class="spot-coin-selected-logo" />
                  : <div class="spot-coin-selected-logo" />}
                <div class="spot-coin-selected-info">
                  <div class="spot-coin-selected-name">{coin.name}</div>
                  <div class="spot-coin-selected-price">
                    {coin.symbol.toUpperCase()}
                    {currentPrice !== null && <span style={{ marginLeft: 8 }}>· {formatUsdPrice(currentPrice)}</span>}
                  </div>
                </div>
                {!isEdit && (
                  <button onClick={() => { setCoin(null); setCurrentPrice(null); }}>Сменить</button>
                )}
              </div>
            ) : (
              <div class="spot-coin-dd" ref={pickerRef}>
                {!pickerOpen ? (
                  <button
                    type="button"
                    class="spot-coin-dd-trigger"
                    onClick={() => setPickerOpen(true)}
                  >
                    <MagnifyingGlass size={14} />
                    <span>Выбрать монету</span>
                    <span class="spot-coin-dd-caret"><CaretDown size={12} weight="bold" /></span>
                  </button>
                ) : (
                  <>
                    <div class="spot-coin-search">
                      <MagnifyingGlass size={14} />
                      <input
                        type="search"
                        autoFocus
                        placeholder="Символ или название…"
                        value={search}
                        onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                      />
                    </div>
                    <div class="spot-coin-dd-panel">
                      <div class="spot-coin-list">
                        {searching && results.length === 0 ? (
                          <div class="spot-coin-dd-state">Загрузка…</div>
                        ) : results.length === 0 ? (
                          <div class="spot-coin-dd-state">
                            Ничего не найдено.<br />Добавь монету в Курсы → Настроить.
                          </div>
                        ) : (
                          results.map((r) => (
                            <button
                              type="button"
                              class="spot-coin-item"
                              key={r.id}
                              onClick={() => { void pickCoin(r); setPickerOpen(false); }}
                            >
                              <span class="spot-coin-item-sym">{r.symbol.toUpperCase()}</span>
                              <span class="spot-coin-item-name">{r.name}</span>
                              <span class="spot-coin-item-go" aria-hidden="true">▸</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div class="spot-form-group">
            <label class="spot-fut-check">
              <input
                type="checkbox"
                checked={isFutures}
                onChange={(e) => setIsFutures((e.target as HTMLInputElement).checked)}
              />
              <span>Фьючерс — цель закрытия вместо сетки TP</span>
            </label>
          </div>

          <div class="spot-form-grid">
            <div class="spot-form-group">
              <label class="spot-form-label">Количество</label>
              <input
                class="spot-form-input"
                inputMode="decimal"
                placeholder="0.0"
                value={qty}
                onInput={(e) => setQty((e.target as HTMLInputElement).value)}
              />
              {coin && <span class="spot-form-hint">единицы: {coin.symbol.toUpperCase()}</span>}
            </div>
            <div class="spot-form-group">
              <label class="spot-form-label">Потрачено $</label>
              <input
                class="spot-form-input"
                inputMode="decimal"
                placeholder="0.00"
                value={totalSpentUsd}
                onInput={(e) => setTotalSpentUsd((e.target as HTMLInputElement).value)}
              />
              {avgPrice !== null && (
                <span class="spot-form-hint">средняя: {formatUsdPrice(avgPrice)}</span>
              )}
            </div>
          </div>

          {!isFutures && (
            <div class="spot-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label class="spot-form-label">Цели фиксации (USD)</label>
                <button
                  class="btn btn-secondary"
                  style={{ fontSize: 'var(--text-xs)', padding: '4px 8px' }}
                  onClick={fillFromAvg}
                  disabled={avgPrice === null}
                  title="Заполнить TP1/TP2/TP3 как +10%/+25%/+50% от средней"
                >
                  Авто +10/25/50%
                </button>
              </div>
              <div class="spot-form-grid-3">
                <input class="spot-form-input" inputMode="decimal" placeholder="TP1" value={tp1} onInput={(e) => setTp1((e.target as HTMLInputElement).value)} />
                <input class="spot-form-input" inputMode="decimal" placeholder="TP2" value={tp2} onInput={(e) => setTp2((e.target as HTMLInputElement).value)} />
                <input class="spot-form-input" inputMode="decimal" placeholder="TP3" value={tp3} onInput={(e) => setTp3((e.target as HTMLInputElement).value)} />
              </div>
              <span class="spot-form-hint">Оставь пустым, если цель пока не задана. ТГ-алерт сработает при достижении.</span>
            </div>
          )}

          {isFutures && (
            <div class="spot-form-group">
              <label class="spot-form-label">Цена закрытия позиции (USD)</label>
              <input
                class="spot-form-input"
                inputMode="decimal"
                placeholder="Цена, при которой позиция закроется на бирже"
                value={closePrice}
                onInput={(e) => setClosePrice((e.target as HTMLInputElement).value)}
              />
              <span class="spot-form-hint">
                {closeDirection === 'up' && '▲ Сработает при росте цены до цели — уведомление в Telegram.'}
                {closeDirection === 'down' && '▼ Сработает при падении цены до цели — уведомление в Telegram.'}
                {closeDirection === null && 'Направление определится по текущей цене. Оставь пустым, если цель пока не задана.'}
              </span>
            </div>
          )}

          {validation && (
            <div style={{ color: 'var(--red)', fontSize: 'var(--text-xs)', padding: '8px 12px', background: 'var(--red-dim)', borderRadius: 'var(--radius-sm)' }}>
              {validation}
            </div>
          )}
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" onClick={onClose} disabled={saving}>Отмена</button>
          <button class="btn btn-primary" onClick={submit} disabled={validation !== null || saving}>
            <Check size={14} />
            <span>{saving ? 'Сохранение...' : (isEdit ? 'Обновить' : 'Добавить')}</span>
          </button>
        </div>
      </div>
    </>
  );
}
